import isEqual from 'lodash/isEqual'
import * as React from 'react'
import { connect } from 'react-redux'
import type { Group } from 'types/group'
import type { State } from 'types/redux'
import type { Track, TrackId } from 'types/track'
import type { DropdownOption } from 'types/ui'
import { bulkSaveTerritories } from '@disco/state-management/actions/ServerActions'
import {
  getTerritories,
  getUserTerritories,
} from '@disco/state-management/selectors/groups'
import CopyMetadataButton from 'components/common/tracks/copy-metadata-button'
import DialogFooter from 'components/modals/dialog-footer'
import WarningDialog from 'components/modals/dialogs/warning-dialog'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
import styles from './index.module.css'

function mapStateToProps(state: State): any {
  return {
    saving: state.server.saving,
    showTerritoryWarning: state.server.showTerritoryWarning,
    error: state.bulk.error,
    territories: getTerritories(state),
    userTerritories: getUserTerritories(state),
  }
}

const actions = {
  bulkSaveTerritories,
}

function aggregateTerritories(
  tracks,
  territories,
): {
  mixed: boolean
  value: DropdownOption[]
} {
  let aggregated

  for (let track of tracks) {
    let trackTerritories = territories
      .filter(t => track.groups.find(groupUrl => t.url === groupUrl))
      .filter(Boolean)
      .map(t => ({
        value: t.id,
        label: t.name,
      }))

    if (!aggregated) {
      aggregated = trackTerritories
    } else {
      if (!isEqual(aggregated, trackTerritories)) {
        return {
          mixed: true,
          value: [],
        }
      }
    }
  }

  return {
    mixed: false,
    value: aggregated || [],
  }
}

class TrackTerritories extends React.Component<
  {
    trackId: TrackId
    tracks: Track[]
    territories: Group[]
    saving: boolean
    userTerritories: Group[]
    bulkSaveTerritories: (ids: TrackId[], add: Group[], remove: Group[]) => any
  },
  {
    showTerritoryWarning: boolean
    selection: {
      mixed: boolean
      value: DropdownOption[]
    }
  }
> {
  state = {
    showTerritoryWarning: false,
    selection: this._initialTerritories(),
  }

  _initialTerritories() {
    return aggregateTerritories(this.props.tracks, this.props.territories)
  }

  _handleSelection = selection => {
    this.setState({
      selection: {
        mixed: false,
        value: selection,
      },
    })
  }
  _saveTerritories = () => {
    const { selection } = this.state
    const { tracks, territories } = this.props
    if (!tracks.length) return
    if (selection.mixed) return
    const remove = territories.filter(
      t => !selection.value.find(s => s.value === t.id),
    )
    const add = territories.filter(t =>
      selection.value.find(s => s.value === t.id),
    )
    return this.props.bulkSaveTerritories(
      tracks.map(t => t.id),
      add,
      remove,
    )
  }
  _onProceed = () => {
    this.setState({
      showTerritoryWarning: false,
    })

    this._saveTerritories()
  }
  _onCloseWarning = () => {
    this.setState({
      showTerritoryWarning: false,
    })
  }
  _onSubmit = () => {
    const { selection } = this.state
    if (selection.mixed) return
    const outsideOfUserTerritories = !this.props.userTerritories.find(ut =>
      selection.value.find(s => s.value === ut.id),
    )

    if (outsideOfUserTerritories) {
      this.setState({
        showTerritoryWarning: true,
      })
    } else {
      this._saveTerritories()
    }
  }

  render() {
    const bulkEditingMode = this.props.tracks.length > 1
    const options = this.props.territories.map(t => ({
      value: t.id,
      label: t.name,
    }))
    let dialog: React.ReactNode = null

    if (this.state.showTerritoryWarning) {
      const _theseTracks = bulkEditingMode ? 'these tracks' : 'this track'

      const _tracks = bulkEditingMode ? 'tracks' : 'track'

      dialog = (
        <WarningDialog
          caption="Warning"
          confirmButtonCaption="Proceed"
          onConfirm={this._onProceed}
          onCancel={this._onCloseWarning}
        >
          You’re about to change the territories on {_theseTracks}, which means
          the {_tracks}&nbsp;<b>may</b> no longer be visible to you. Are you
          sure you want to proceed?
        </WarningDialog>
      )
    }

    return (
      <div>
        {dialog}
        <div className={styles.chosenTerritories}>
          <CustomDropdown
            multi
            placeholder={
              this.state.selection.mixed ? 'Mixed' : 'Select Territory'
            }
            options={options}
            value={this.state.selection.value}
            onChange={this._handleSelection}
          />
        </div>
        <DialogFooter
          primary={
            <ButtonContainer>
              {this.props.trackId && (
                <CopyMetadataButton trackIds={[this.props.trackId]} />
              )}
              <Button
                appearance="primary"
                caption="Save territories"
                disabled={isEqual(
                  this.state.selection,
                  this._initialTerritories(),
                )}
                progressIndicator={this.props.saving}
                progressCaption="Saving territories"
                onClick={this._onSubmit}
              />
            </ButtonContainer>
          }
        />
      </div>
    )
  }
}

export default connect(mapStateToProps, actions)(TrackTerritories) as any
